/**
 * BlueBean API(顧客発着信履歴出力API・発着信履歴ダウンロードAPI)の返り値から、1日分の
 * 「オペレーター(内部ID) × 業務コード」の 架電数・通話時間・取得件数 を作る(2026-09-17。docs/specs/bluebean-api.md §12)。
 * 通信も認証情報もここには置かない(純粋な計算だけ)。呼び出しはサーバー側(functions/)から。
 *
 * - API のオペレーターは **BlueBean の内部ID**(ログインID(SR2xxx など)とは別。ソフツー回答 B-3・B-6)。
 *   ここでは内部IDのまま集計し、BB ID への読み替えは画面側で マスタの bb_internal_id と候補(suggestBbIds)で行う
 * - 取得件数の判定は CSV 取込と同じ `aggregateActivity`(アポ〇・同じ顧客はその日1件・時間帯は更新時刻の時台)
 * - API のリアクションは顧客の「最新」だけ(§3)。そのため **その顧客の最後の通話の日** にだけ取得を付ける
 *   (現在の取込元 顧客情報レポートの「更新日時(最新)」「最終保存オペレーター(最新)」と同じ考え方)
 * - 顧客の個人情報(名前・電話番号)は重複判定にメモリ上で使うだけで、返り値に残さない。CDR の発信元・着信先・録音・文字起こしは読まない
 */
import { HOUR_BANDS, type Company, type DateStr } from '@core/lib/types';
import { aggregateActivity, normalizeDateTime, normalizeCode, APPOINTMENT_REACTION } from './activity';
import type { BbApiCall, BbApiCustomer } from './bluebeanApi';
import type { ParsedActivityRow } from './types';

/** オペレーター(内部ID) × 業務コード の1日分 */
export type ApiOperatorCodeStat = {
  /** オペレーターにつながった通話の本数(内線・着信・発信すべて) */
  calls: number;
  /** うち コール結果=完了 */
  success: number;
  /** CDR と Group_CallId で突き合わせた通話時間(秒)。CDR を取れなかった日は null */
  talk_sec: number | null;
  /** 通話時間が付いた本数(calls との差は CDR に見つからなかった通話) */
  talk_matched: number;
  /** その会社の最初と最後の通話の開始時刻 'HH:MM:SS' */
  first_at: string | null;
  last_at: string | null;
  /** 取得(CSV 取込と同じ判定) */
  appointments: number;
  /** 架電の時間帯別(HOUR_BANDS と同じ順) */
  calls_by_hour?: number[];
  /** 取得の時間帯別(HOUR_BANDS と同じ順) */
  by_hour: number[];
};

export type ApiOperatorDaily = {
  /** API の operator_name(BlueBean の表示名) */
  name: string;
  by_code: Record<string, ApiOperatorCodeStat>;
};

/** 業務(会社)1件分の取得結果 */
export type ApiCampaignResult = {
  code: string;
  campaign_id: number;
  customers: number;
  calls: number;
  error: string | null;
};

/** bb_api_daily/{date} */
export type BbApiDaily = {
  date: DateStr;
  /** ok=全部取れた / partial=一部の業務かCDRが取れなかった / error=何も取れなかった */
  status: 'ok' | 'partial' | 'error';
  fetched_at?: unknown;
  /** 'schedule'(毎朝の自動) か 実行した管理者のメール */
  fetched_by: string;
  campaigns: ApiCampaignResult[];
  cdr: { rows: number; matched: number; format: CdrFormat | null; error: string | null } | null;
  /** 取得件数の API(顧客発着信履歴出力API)がこの BlueBean で使えるか(2026-09-18。V10.17.3 は 10.17.4 未満のため確かめる) */
  api_check?: ApiCheck | null;
  /** 通話明細(CDR)から数えた、オペレーター(CDR の「オペレータ」列の値)ごとの通話。会社には分けられない */
  cdr_operators?: Record<string, CdrOperatorStat>;
  /** 内部ID → 集計 */
  operators: Record<string, ApiOperatorDaily>;
  /** オペレーターにつながっていない通話(PD発信で operator_id=0 など) */
  unconnected_calls: number;
  messages: string[];
};

// ---- 値の正規化 -------------------------------------------------------------

export type CallStatus = 'success' | 'cancel' | 'dialing' | 'talking' | 'other';

/** コール結果・状態(英語コードと日本語表示の両方) */
export function normalizeCallStatus(s: string | null | undefined): CallStatus {
  const t = (s ?? '').trim().toUpperCase();
  if (t === 'SUCCESS' || t === '完了') return 'success';
  if (t === 'CANCEL' || t === 'キャンセル') return 'cancel';
  if (t === 'DIALING' || t === '発着信中') return 'dialing';
  if (t === 'TALKING' || t === '通話中') return 'talking';
  return 'other';
}

/** 'HH:MM:SS' / 'MM:SS' / 秒の数字 → 秒。読めなければ null */
export function durationSec(s: string | null | undefined): number | null {
  const t = (s ?? '').trim();
  if (t === '') return null;
  if (/^\d+$/.test(t)) return Number(t);
  const m = t.match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?$/);
  if (!m) return null;
  return m[3] !== undefined ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) : Number(m[1]) * 60 + Number(m[2]);
}

/** オペレーターにつながっていない通話(operator_id が空・0) */
export function isUnconnected(operatorId: string): boolean {
  return operatorId === '' || operatorId === '0';
}

export const callStart = (c: BbApiCall) => normalizeDateTime(c.start_date ?? '') ?? normalizeDateTime(c.end_date ?? '') ?? '';
export const callEnd = (c: BbApiCall) => normalizeDateTime(c.end_date ?? '') ?? callStart(c);

export function isAppointment(reaction: string): boolean {
  return reaction.trim().replace(/[○◯]/g, '〇') === APPOINTMENT_REACTION;
}

// ---- 顧客発着信履歴出力API ----------------------------------------------------

/**
 * 分割して取った返り値を1つにまとめる(1回1万件の上限で時間帯を刻んだとき、同じ顧客が複数回に分かれて返るため)。
 * 顧客は master.id、通話は group_callid＋発着信時間 で重複を除く
 */
export function mergeCustomers(chunks: BbApiCustomer[][]): BbApiCustomer[] {
  const byId = new Map<string, BbApiCustomer>();
  const loose: BbApiCustomer[] = [];
  for (const chunk of chunks) {
    for (const cust of chunk) {
      const id = (cust.master?.id ?? '').toString();
      if (id === '') {
        loose.push(cust);
        continue;
      }
      const prev = byId.get(id);
      if (!prev) {
        byId.set(id, { master: cust.master, calls: [...(cust.calls ?? [])] });
        continue;
      }
      const seen = new Set((prev.calls ?? []).map((c) => `${c.group_callid ?? ''}|${c.start_date ?? ''}|${c.operator_id ?? ''}`));
      for (const c of cust.calls ?? []) {
        const key = `${c.group_callid ?? ''}|${c.start_date ?? ''}|${c.operator_id ?? ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          (prev.calls ??= []).push(c);
        }
      }
    }
  }
  return [...byId.values(), ...loose];
}

/** 1業務分の集計(通話時間は後から attachTalkTime で付ける) */
export type CampaignSummary = {
  code: string;
  customers: number;
  calls: number;
  unconnected: number;
  operators: Record<string, ApiOperatorDaily>;
  /** Group_CallId → その通話のオペレーター(CDR との突き合わせ用。保存しない) */
  callIndex: Map<string, { operatorId: string; code: string }[]>;
};

function emptyStat(): ApiOperatorCodeStat {
  return { calls: 0, success: 0, talk_sec: null, talk_matched: 0, first_at: null, last_at: null, appointments: 0, by_hour: HOUR_BANDS.map(() => 0), calls_by_hour: HOUR_BANDS.map(() => 0) };
}

/**
 * 顧客発着信履歴出力API の data[] (1業務・1日分)を、オペレーター(内部ID) × 業務コード にまとめる。
 * @param code 呼び出しに使った業務のコード(会社マスタの BB業務コード)
 */
export function summarizeCampaign(data: BbApiCustomer[], code: string, date: DateStr, companies: Company[]): CampaignSummary {
  const bizCode = normalizeCode(code);
  const operators: Record<string, ApiOperatorDaily> = {};
  const callIndex = new Map<string, { operatorId: string; code: string }[]>();
  const apptRows: ParsedActivityRow[] = [];
  let calls = 0;
  let unconnected = 0;
  let customers = 0;
  let rowNo = 0;

  const statOf = (operatorId: string, name: string) => {
    const op = (operators[operatorId] ??= { name, by_code: {} });
    if (!op.name && name) op.name = name;
    return (op.by_code[bizCode] ??= emptyStat());
  };

  for (const cust of data) {
    const all = (cust.calls ?? []).filter((c) => callStart(c) !== '');
    const today = all.filter((c) => callStart(c).slice(0, 10) === date);
    if (today.length === 0) continue;
    customers++;
    for (const c of today) {
      calls++;
      const operatorId = (c.operator_id ?? '').toString().trim();
      if (isUnconnected(operatorId)) {
        unconnected++;
        continue;
      }
      const stat = statOf(operatorId, (c.operator_name ?? '').toString().trim());
      stat.calls++;
      if (normalizeCallStatus(c.call_status) === 'success') stat.success++;
      const hms = callStart(c).slice(11);
      const ci = (HOUR_BANDS as readonly number[]).indexOf(Number(hms.slice(0, 2)));
      if (ci >= 0 && stat.calls_by_hour) stat.calls_by_hour[ci] = (stat.calls_by_hour[ci] ?? 0) + 1;
      if (stat.first_at === null || hms < stat.first_at) stat.first_at = hms;
      if (stat.last_at === null || hms > stat.last_at) stat.last_at = hms;
      const gid = (c.group_callid ?? '').toString().trim();
      if (gid) callIndex.set(gid, [...(callIndex.get(gid) ?? []), { operatorId, code: bizCode }]);
    }

    // 取得: 顧客の最新リアクションがアポ〇で、オペレーターにつながった最後の通話がこの日のもの
    if (!isAppointment((cust.master?.response ?? '').toString())) continue;
    const connected = all.filter((c) => !isUnconnected((c.operator_id ?? '').toString().trim()));
    const last = [...connected].sort((a, b) => callEnd(a).localeCompare(callEnd(b)) || callStart(a).localeCompare(callStart(b))).pop();
    if (!last || callStart(last).slice(0, 10) !== date) continue;
    rowNo++;
    const phone = (last.phone ?? '').toString().replace(/\D/g, '');
    const customerId = (cust.master?.id ?? '').toString();
    apptRows.push({
      at: callStart(last),
      updated_at: callEnd(last),
      bb_id: (last.operator_id ?? '').toString().trim(),
      operator_name: (last.operator_name ?? '').toString().trim(),
      business_code: bizCode,
      customer_key: phone !== '' ? `tel:${phone}` : customerId !== '' ? `cid:${customerId}` : `row:${rowNo}`,
      is_cancel: false,
      source_row: rowNo,
    });
  }

  const agg = aggregateActivity(apptRows, { date, companies });
  const nameOf = new Map(apptRows.map((r) => [r.bb_id, r.operator_name]));
  for (const [operatorId, perCode] of agg.stats) {
    for (const s of perCode.values()) {
      const stat = statOf(operatorId, nameOf.get(operatorId) ?? '');
      stat.appointments += s.appointments;
      stat.by_hour = stat.by_hour.map((v, i) => v + (s.by_hour[i] ?? 0));
    }
  }
  return { code: bizCode, customers, calls, unconnected, operators, callIndex };
}

// ---- 発着信履歴ダウンロードAPI(CDR) ------------------------------------------

/** api = API(10.19.0 以降。先頭列 Group_CallId・オペレータは内部ID) / screen = 管理画面から出したCSV(先頭列 ID・オペレータは「氏名 (SN1109)」) */
export type CdrFormat = 'api' | 'screen' | 'legacy';

/** 取得件数の API が使えるか。available=使える / not_found=この版では無い見込み / auth_error=ID・パスワードが通らない / error=その他 */
export type ApiCheck = { call_history: 'available' | 'not_found' | 'auth_error' | 'error'; detail: string };

/** CDR のオペレーター1人・1日分 */
export type CdrOperatorStat = {
  /** そのオペレーターの行(通話)の本数 */
  calls: number;
  /** うち通話時間が1秒以上 */
  talked: number;
  talk_sec: number;
  first_at: string | null;
  last_at: string | null;
};

/** CDR 1行(使う列だけ。発信元・着信先・録音・文字起こし・要約は読まない) */
export type CdrRow = {
  id: string;
  type: string;
  total_sec: number | null;
  talk_sec: number | null;
  status: CallStatus;
  at: string | null;
  operator: string;
};

/** RFC 4180 の CSV を行の配列に(改行を含むセルにも対応) */
function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const src = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** CDR の CSV を読む。形式が分からなければ format=null・行なし */
export function parseCdrCsv(text: string): { format: CdrFormat | null; rows: CdrRow[] } {
  const table = parseCsvText(text);
  const header = (table[0] ?? []).map((h) => h.trim());
  const idx = (name: string) => header.indexOf(name);
  // legacy = 8.5.2版の API(V10.19.0 未満。先頭列が「種類」で Group_CallId が無い)
  const format: CdrFormat | null = header[0] === 'Group_CallId' ? 'api' : header[0] === 'ID' ? 'screen' : header[0] === '種類' ? 'legacy' : null;
  if (!format) return { format: null, rows: [] };
  const I = { type: idx('種類'), total: idx('総時間'), talk: idx('通話時間'), status: idx('状態'), at: idx('発着信時間'), operator: idx('オペレータ') };
  const cell = (r: string[], i: number) => (i >= 0 ? (r[i] ?? '').trim() : '');
  const rows = table.slice(1).map((r) => ({
    id: format === 'legacy' ? '' : cell(r, 0),
    type: cell(r, I.type),
    total_sec: durationSec(cell(r, I.total)),
    talk_sec: durationSec(cell(r, I.talk)),
    status: normalizeCallStatus(cell(r, I.status)),
    at: normalizeDateTime(cell(r, I.at)),
    operator: cell(r, I.operator),
  }));
  return { format, rows };
}

/** CDR をオペレーター(「オペレータ」列の値)ごとに数える。オペレーターが空・「-」の行(つながらなかった通話)は除く */
export function summarizeCdrByOperator(rows: CdrRow[], date: DateStr): Record<string, CdrOperatorStat> {
  const out: Record<string, CdrOperatorStat> = {};
  for (const r of rows) {
    const op = r.operator.trim();
    if (op === '' || op === '-' || op === '0') continue;
    if (r.at && r.at.slice(0, 10) !== date) continue;
    const s = (out[op] ??= { calls: 0, talked: 0, talk_sec: 0, first_at: null, last_at: null });
    s.calls++;
    if ((r.talk_sec ?? 0) > 0) {
      s.talked++;
      s.talk_sec += r.talk_sec ?? 0;
    }
    const hms = r.at?.slice(11) ?? null;
    if (hms && (s.first_at === null || hms < s.first_at)) s.first_at = hms;
    if (hms && (s.last_at === null || hms > s.last_at)) s.last_at = hms;
  }
  return out;
}

/**
 * CDR(API形式)の通話時間を、Group_CallId で 各通話のオペレーター×業務 に足す。
 * 同じ Group_CallId に同じオペレーターの行が複数あれば、長いほう1行だけを使う(同じ通話の二重計上を防ぐ)
 * @returns 突き合わせできた通話の本数
 */
export function attachTalkTime(operators: Record<string, ApiOperatorDaily>, callIndex: Map<string, { operatorId: string; code: string }[]>, cdr: CdrRow[]): number {
  // Group_CallId → オペレーター → 通話時間の最大
  const talkBy = new Map<string, Map<string, number>>();
  for (const r of cdr) {
    if (!r.id || r.talk_sec === null) continue;
    const per = talkBy.get(r.id) ?? new Map<string, number>();
    const key = r.operator;
    per.set(key, Math.max(per.get(key) ?? 0, r.talk_sec));
    talkBy.set(r.id, per);
  }
  // CDR を取れた日は、通話時間の入れ物を 0 から始める
  for (const op of Object.values(operators)) for (const s of Object.values(op.by_code)) s.talk_sec ??= 0;
  let matched = 0;
  for (const [gid, entries] of callIndex) {
    const per = talkBy.get(gid);
    if (!per) continue;
    for (const e of entries) {
      // オペレータ列が内部IDで一致する行を優先。無ければ、その通話にオペレーターが1人だけのときに限り使う
      const sec = per.get(e.operatorId) ?? (per.size === 1 && entries.length === 1 ? [...per.values()][0] : undefined);
      if (sec === undefined) continue;
      const stat = operators[e.operatorId]?.by_code[e.code];
      if (!stat) continue;
      stat.talk_sec = (stat.talk_sec ?? 0) + sec;
      stat.talk_matched++;
      matched++;
    }
  }
  return matched;
}

/** 複数業務の集計を1日分にまとめる */
export function mergeCampaignSummaries(list: CampaignSummary[]): { operators: Record<string, ApiOperatorDaily>; callIndex: Map<string, { operatorId: string; code: string }[]>; unconnected: number } {
  const operators: Record<string, ApiOperatorDaily> = {};
  const callIndex = new Map<string, { operatorId: string; code: string }[]>();
  let unconnected = 0;
  for (const s of list) {
    unconnected += s.unconnected;
    for (const [id, op] of Object.entries(s.operators)) {
      const into = (operators[id] ??= { name: op.name, by_code: {} });
      if (!into.name && op.name) into.name = op.name;
      for (const [code, stat] of Object.entries(op.by_code)) {
        const prev = into.by_code[code];
        into.by_code[code] = prev
          ? {
              calls: prev.calls + stat.calls,
              success: prev.success + stat.success,
              talk_sec: prev.talk_sec === null && stat.talk_sec === null ? null : (prev.talk_sec ?? 0) + (stat.talk_sec ?? 0),
              talk_matched: prev.talk_matched + stat.talk_matched,
              first_at: [prev.first_at, stat.first_at].filter((x): x is string => !!x).sort()[0] ?? null,
              last_at: [prev.last_at, stat.last_at].filter((x): x is string => !!x).sort().pop() ?? null,
              appointments: prev.appointments + stat.appointments,
              by_hour: prev.by_hour.map((v, i) => v + (stat.by_hour[i] ?? 0)),
            }
          : { ...stat, by_hour: [...stat.by_hour] };
      }
    }
    for (const [gid, entries] of s.callIndex) callIndex.set(gid, [...(callIndex.get(gid) ?? []), ...entries]);
  }
  return { operators, callIndex, unconnected };
}

// ---- 取得範囲の分割(1回1万件の上限) ----------------------------------------------

/** 'YYYY-MM-DD HH:MM:SS' の範囲を半分に分ける。これ以上分けられない(10分未満)なら null */
export function splitRange(from: string, to: string): [[string, string], [string, string]] | null {
  const toMs = (s: string) => Date.parse(`${s.replace(' ', 'T')}Z`);
  const a = toMs(from);
  const b = toMs(to);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b - a < 10 * 60 * 1000) return null;
  const mid = a + Math.floor((b - a) / 2 / 1000) * 1000;
  const fmt = (ms: number) => new Date(ms).toISOString().slice(0, 19).replace('T', ' ');
  return [
    [from, fmt(mid)],
    [fmt(mid + 1000), to],
  ];
}

// ---- 内部ID → BB ID の候補 ------------------------------------------------------

export type BbIdSource = 'master' | 'name_has_id' | 'op_report_name' | 'master_name' | 'op_report_calls';
export const BB_ID_SOURCE_LABELS: Record<BbIdSource, string> = {
  master: 'マスタに登録済み',
  name_has_id: '表示名のID',
  op_report_name: 'OPレポートの表示名',
  master_name: 'マスタの氏名',
  op_report_calls: 'OPレポートの通話回数・通話時間',
};

/** OPレポートの1人・1日分(通話回数・通話時間合計) */
export type OpCallStat = { bb_id: string; talk_count: number | null; talk_sec: number | null };

/**
 * 名前の無い内部ID(8.5.2版の CDR)を、その日の OPレポートの 通話回数・通話時間合計 と照らして BB ID を推す。
 * 通話回数の差が2回以内・通話時間の差が3%(最低60秒)以内の人が1人だけか、2人以上いても一番近い人が十分近いときだけ候補にする
 */
export function matchByCallStats(stat: Pick<CdrOperatorStat, 'talked' | 'talk_sec'>, opRows: OpCallStat[]): string | null {
  const scored = opRows
    .filter((o) => o.talk_count !== null && o.talk_sec !== null && (o.talk_count ?? 0) > 0)
    .map((o) => {
      const dc = Math.abs((o.talk_count ?? 0) - stat.talked);
      const ds = Math.abs((o.talk_sec ?? 0) - stat.talk_sec);
      const tol = Math.max(60, (o.talk_sec ?? 0) * 0.03);
      return { bb_id: o.bb_id, ok: dc <= 2 && ds <= tol, score: dc + ds / 60 };
    })
    .filter((s) => s.ok)
    .sort((a, b) => a.score - b.score);
  const [best, second] = scored;
  if (!best) return null;
  return !second || second.score - best.score >= 3 ? best.bb_id : null;
}

export type AccountRef = { bb_id: string; internal_id: string | null; person_name: string; business: 'kyutoki' | 'fuyohin'; active: boolean };

export type BbIdSuggestion = {
  internal_id: string;
  name: string;
  bb_id: string | null;
  source: BbIdSource | null;
  /** 決めきれなかったときの候補 */
  candidates: string[];
  /** 同じ BB ID を別の内部IDも指している */
  conflict: boolean;
};

/** 氏名の比較用(空白を除く・全角英字を半角に) */
export function nameKey(s: string): string {
  return s
    .replace(/[\s　]/g, '')
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .toUpperCase();
}

const BB_ID_IN_NAME = /(?:^|[^A-Z0-9])([A-Z]{2,4}\d{2,4})(?![A-Z0-9])/;

/**
 * API の内部IDごとに、どの BB ID(ログインID)かの候補を出す。優先順:
 *  1. マスタの bb_internal_id に登録済み
 *  2. 表示名に BB ID が含まれる(例「架空　一郎 (SN9001)」)
 *  3. その日の OPレポートの表示名と一致(BlueBean の表示名そのもの)
 *  4. マスタの氏名と一致(末尾 W は不用品のアカウント)。有効なアカウントが1つに決まるときだけ
 *  5. (名前が無いとき)その日の OPレポートの 通話回数・通話時間 と一致(callStats を渡したとき)
 */
export function suggestBbIds(
  apiOperators: { internal_id: string; name: string }[],
  accounts: AccountRef[],
  displayNames: { bb_id: string; name: string }[],
  callStats?: { cdr: Record<string, Pick<CdrOperatorStat, 'talked' | 'talk_sec'>>; op: OpCallStat[] },
): BbIdSuggestion[] {
  const byInternal = new Map(accounts.filter((a) => a.internal_id).map((a) => [a.internal_id!, a.bb_id]));
  const knownIds = new Map(accounts.map((a) => [a.bb_id.toUpperCase(), a.bb_id]));
  const byDisplay = new Map<string, Set<string>>();
  for (const d of displayNames) {
    const k = nameKey(d.name);
    if (k) byDisplay.set(k, (byDisplay.get(k) ?? new Set()).add(d.bb_id));
  }
  const out: BbIdSuggestion[] = apiOperators.map(({ internal_id, name }) => {
    const base = { internal_id, name, candidates: [] as string[], conflict: false };
    const registered = byInternal.get(internal_id);
    if (registered) return { ...base, bb_id: registered, source: 'master' as const };
    const inName = nameKey(name).match(BB_ID_IN_NAME)?.[1];
    if (inName && knownIds.has(inName)) return { ...base, bb_id: knownIds.get(inName)!, source: 'name_has_id' as const };
    const disp = [...(byDisplay.get(nameKey(name)) ?? [])];
    if (disp.length === 1) return { ...base, bb_id: disp[0]!, source: 'op_report_name' as const };
    const key = nameKey(name);
    const white = /W$/.test(key);
    const person = white ? key.slice(0, -1) : key;
    const matches = accounts.filter((a) => a.active && nameKey(a.person_name).replace(/W$/, '') === person);
    const narrowed = matches.filter((a) => (white ? a.business === 'fuyohin' : a.business === 'kyutoki'));
    const pick = narrowed.length === 1 ? narrowed : matches.length === 1 ? matches : [];
    if (pick.length === 1) return { ...base, bb_id: pick[0]!.bb_id, source: 'master_name' as const };
    const cdrStat = callStats?.cdr[internal_id];
    const byCalls = cdrStat ? matchByCallStats(cdrStat, callStats!.op) : null;
    if (byCalls) return { ...base, bb_id: byCalls, source: 'op_report_calls' as const };
    return { ...base, bb_id: null, source: null, candidates: [...new Set([...disp, ...matches.map((a) => a.bb_id)])] };
  });
  const count = new Map<string, number>();
  for (const s of out) if (s.bb_id) count.set(s.bb_id, (count.get(s.bb_id) ?? 0) + 1);
  for (const s of out) if (s.bb_id && (count.get(s.bb_id) ?? 0) > 1) s.conflict = true;
  return out;
}

// ---- CSV 取込の結果との比較 ------------------------------------------------------

export type ApiCompareRow = {
  bb_id: string | null;
  /** API側の内部ID(CSVにだけある行は null) */
  internal_id: string | null;
  api_name: string;
  code: string;
  api: ApiOperatorCodeStat | null;
  /** CSV 取込(bb_daily_stats)の取得。その BB ID × 業務の取込が無ければ null */
  csv_appointments: number | null;
};

/**
 * API の集計と CSV 取込の取得件数を、BB ID × 業務コード で並べる。
 * @param resolve 内部ID → BB ID(マスタか候補。決まらなければ null)
 * @param csv BB ID → 業務コード → 取得件数(bb_daily_stats の activity)
 */
export function compareWithCsv(operators: Record<string, ApiOperatorDaily>, resolve: (internalId: string) => string | null, csv: Map<string, Map<string, number>>): ApiCompareRow[] {
  const rows: ApiCompareRow[] = [];
  const seen = new Set<string>();
  for (const [internalId, op] of Object.entries(operators)) {
    const bbId = resolve(internalId);
    for (const [code, stat] of Object.entries(op.by_code)) {
      const csvCount = bbId ? (csv.get(bbId)?.get(code) ?? null) : null;
      if (bbId) seen.add(`${bbId}|${code}`);
      rows.push({ bb_id: bbId, internal_id: internalId, api_name: op.name, code, api: stat, csv_appointments: csvCount });
    }
  }
  for (const [bbId, perCode] of csv) {
    for (const [code, n] of perCode) {
      if (seen.has(`${bbId}|${code}`)) continue;
      rows.push({ bb_id: bbId, internal_id: null, api_name: '', code, api: null, csv_appointments: n });
    }
  }
  return rows.sort((a, b) => (a.bb_id ?? '~').localeCompare(b.bb_id ?? '~') || a.code.localeCompare(b.code) || (a.internal_id ?? '').localeCompare(b.internal_id ?? ''));
}
